'use client'

import { useEffect } from 'react'
import type { Session } from '@supabase/supabase-js'
import { getSupabaseClient } from '@/lib/supabase-client'
import { resetUser } from '@/instrumentation-client'
import {
  fetchAfterSignInSync,
  fetchSessionRestoreSync,
  type SyncInFlightRef
} from '@/contexts/auth-account-sync'
import type { AuthStateHandlers } from '@/contexts/auth-operations'

const supabase = getSupabaseClient()

export interface SessionSyncInput {
  handlers: AuthStateHandlers
  syncInFlightRef: SyncInFlightRef
}

function restoreSession(input: SessionSyncInput): void {
  void fetchSessionRestoreSync({
    syncInFlightRef: input.syncInFlightRef,
    handlers: input.handlers
  }).catch((error) => {
    console.error('Error restoring authenticated session:', error)
    input.handlers.setIsLoading(false)
  })
}

function syncAfterSignIn(accessToken: string, input: SessionSyncInput): void {
  const syncPromise = fetchAfterSignInSync({
    accessToken,
    syncInFlightRef: input.syncInFlightRef,
    handlers: input.handlers
  })

  if (!syncPromise) {
    return
  }

  void syncPromise.catch((error) => {
    console.error('Error syncing after sign-in:', error)
    input.handlers.setIsLoading(false)
  })
}

function resetSignedOutSession({ handlers }: SessionSyncInput): void {
  resetUser()
  handlers.setUser(null)
  handlers.setIsLoading(false)
}

function handleInitialRestore(
  session: Session | null,
  input: SessionSyncInput,
  initialRestoreHandled: { current: boolean }
): void {
  if (initialRestoreHandled.current) {
    return
  }

  initialRestoreHandled.current = true

  if (session?.user) {
    restoreSession(input)
    return
  }

  input.handlers.setIsLoading(false)
}

function handleAuthStateChange(
  event: string,
  session: Session | null,
  input: SessionSyncInput,
  initialRestoreHandled: { current: boolean }
): void {
  switch (event) {
    case 'INITIAL_SESSION':
      handleInitialRestore(session, input, initialRestoreHandled)
      break
    case 'SIGNED_IN':
      if (session?.access_token) {
        syncAfterSignIn(session.access_token, input)
      } else {
        input.handlers.setIsLoading(false)
      }
      break
    case 'SIGNED_OUT':
      resetSignedOutSession(input)
      break
    default:
      break
  }
}

export function useInitialSessionSync({
  handlers,
  syncInFlightRef
}: SessionSyncInput): void {
  useEffect(() => {
    const input = { handlers, syncInFlightRef }
    const initialRestoreHandled = { current: false }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      handleAuthStateChange(event, session, input, initialRestoreHandled)
    })

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleInitialRestore(session, input, initialRestoreHandled)
    })

    return () => subscription.unsubscribe()
  }, [handlers, syncInFlightRef])
}
